import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip,
  IconButton, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, Snackbar, Alert,
  Tooltip, CircularProgress, Fade, Avatar,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Person as PersonIcon, Phone as PhoneIcon,
  Email as EmailIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { contactService } from '../services/contactService';

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof', 'Rev'];

const initialFormState = {
  title: '',
  forename: '',
  surname: '',
  email: '',
  home_phone: '',
  work_phone: '',
  mobile_phone: '',
  category: '',
  office: '',
  negotiator: '',
  company: '',
  marketing_consent: 'not_asked',
};

function getInitials(forename, surname) {
  return `${(forename || '')[0] || ''}${(surname || '')[0] || ''}`.toUpperCase() || '?';
}

function getAvatarColor(name) {
  const colors = ['#6200EE', '#3700B3', '#03DAC6', '#018786', '#7B1FA2', '#1976D2', '#0288D1'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contactService.getAll();
      setContacts(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      showSnackbar('Failed to load contacts.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredContacts = contacts.filter((c) => {
    const q = search.toLowerCase();
    return (
      `${c.forename} ${c.surname}`.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.mobile_phone || '').includes(q) ||
      (c.home_phone || '').includes(q)
    );
  });

  const handleOpenCreate = () => {
    setEditContact(null);
    setForm(initialFormState);
    setDialogOpen(true);
  };

  const handleOpenEdit = (contact) => {
    setEditContact(contact);

    let formattedTitle = '';
    if (contact.title) {
      formattedTitle = contact.title.charAt(0).toUpperCase() + contact.title.slice(1).toLowerCase();
    }

    let formattedConsent = 'not_asked';
    if (contact.marketing_consent === true) formattedConsent = 'granted';
    else if (contact.marketing_consent === false) formattedConsent = 'denied';

    setForm({
      ...initialFormState,
      ...contact,
      title: formattedTitle,
      marketing_consent: formattedConsent,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditContact(null);
    setForm(initialFormState);
  };

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.forename.trim() || !form.surname.trim()) {
      showSnackbar('First name and last name are required.', 'error');
      return;
    }
    setSaving(true);

    const payload = { ...form };
    if (payload.title) payload.title = payload.title.toLowerCase();
    payload.marketing_consent = payload.marketing_consent === 'granted';

    try {
      if (editContact) {
        await contactService.update(editContact.id, payload);
        showSnackbar('Contact updated successfully.');
      } else {
        await contactService.create(payload);
        showSnackbar('Contact created successfully.');
      }
      handleCloseDialog();
      fetchContacts();
    } catch (e) {
      let errMsg = 'Failed to save contact.';
      if (e.response && e.response.data) {
        errMsg = typeof e.response.data === 'object' 
          ? Object.entries(e.response.data).map(([k, v]) => `${k}: ${v}`).join(' | ') 
          : e.response.data;
      }
      showSnackbar(errMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await contactService.delete(contactToDelete.id);
      showSnackbar('Contact deleted.');
      setDeleteDialogOpen(false);
      setContactToDelete(null);
      fetchContacts();
    } catch (e) {
      showSnackbar('Failed to delete contact.', 'error');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh', background: '#F4F6FB' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E', letterSpacing: '-0.5px' }}>
            Contacts
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
            Manage all your buyers, sellers, landlords, and applicants in one place.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchContacts} sx={{ border: '1px solid #E0E0E0', borderRadius: 2 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              background: 'linear-gradient(135deg, #6200EE, #9E47FF)',
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              boxShadow: '0 4px 15px rgba(98,0,238,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3700B3, #6200EE)' },
            }}
          >
            Add Contact
          </Button>
        </Box>
      </Box>

      {/* Search bar */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #E8EAF0' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9E9E9E' }} /></InputAdornment>,
            sx: { borderRadius: 2, background: '#FAFBFC' },
          }}
        />
      </Paper>

      {/* Stats row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Contacts', value: contacts.length, color: '#6200EE' },
          { label: 'Shown', value: filteredContacts.length, color: '#018786' },
        ].map((stat) => (
          <Paper key={stat.label} elevation={0} sx={{
            p: 2, borderRadius: 3, border: '1px solid #E8EAF0', display: 'flex',
            alignItems: 'center', gap: 2, minWidth: 160,
          }}>
            <Box sx={{ width: 10, height: 36, background: stat.color, borderRadius: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>{stat.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E8EAF0', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #F8F5FF, #F0F4FF)' }}>
                <TableCell sx={{ fontWeight: 700, color: '#444', py: 2 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#444' }}><EmailIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#444' }}><PhoneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />Mobile</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#444' }}>Home Phone</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#444' }}>Work Phone</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#444', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={36} sx={{ color: '#6200EE' }} />
                  </TableCell>
                </TableRow>
              ) : filteredContacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                    <PersonIcon sx={{ fontSize: 56, color: '#E0E0E0', mb: 1 }} />
                    <Typography sx={{ color: '#BDBDBD', fontWeight: 500 }}>No contacts found</Typography>
                    <Button
                      onClick={handleOpenCreate}
                      variant="outlined"
                      size="small"
                      sx={{ mt: 2, borderRadius: 2, borderColor: '#6200EE', color: '#6200EE' }}
                    >
                      Add your first contact
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredContacts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((contact) => (
                    <Fade in key={contact.id}>
                      <TableRow
                        hover
                        sx={{
                          transition: 'background 0.15s',
                          '&:hover': { background: '#F8F5FF' },
                          '&:last-child td': { border: 0 },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 38, height: 38, fontSize: 14, fontWeight: 700,
                                background: getAvatarColor(`${contact.forename}${contact.surname}`),
                              }}
                            >
                              {getInitials(contact.forename, contact.surname)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                {contact.title && `${contact.title} `}{contact.forename} {contact.surname}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#555', fontSize: 13 }}>{contact.email || '—'}</TableCell>
                        <TableCell sx={{ color: '#555', fontSize: 13 }}>{contact.mobile_phone || '—'}</TableCell>
                        <TableCell sx={{ color: '#555', fontSize: 13 }}>{contact.home_phone || '—'}</TableCell>
                        <TableCell sx={{ color: '#555', fontSize: 13 }}>{contact.work_phone || '—'}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenEdit(contact)} sx={{ color: '#6200EE', mr: 0.5 }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteClick(contact)} sx={{ color: '#F44336' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </Fade>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredContacts.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: '1px solid #F0F0F0' }}
        />
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #6200EE, #9E47FF)',
          color: '#fff',
          fontWeight: 700,
          py: 2.5,
        }}>
          {editContact ? 'Edit Contact' : 'New Contact'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                select label="Title" name="title" fullWidth size="small"
                value={form.title} onChange={handleFormChange}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {TITLE_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4.5}>
              <TextField
                label="First Name *" name="forename" fullWidth size="small"
                value={form.forename} onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={4.5}>
              <TextField
                label="Last Name *" name="surname" fullWidth size="small"
                value={form.surname} onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email" name="email" fullWidth size="small" type="email"
                value={form.email} onChange={handleFormChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 16, color: '#9E9E9E' }} /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile Phone" name="mobile_phone" fullWidth size="small"
                value={form.mobile_phone} onChange={handleFormChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: '#9E9E9E' }} /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Home Phone" name="home_phone" fullWidth size="small"
                value={form.home_phone} onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Work Phone" name="work_phone" fullWidth size="small"
                value={form.work_phone} onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: '#666' }}>Organisation & Consent</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category ID" name="category" fullWidth size="small"
                value={form.category} onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company ID" name="company" fullWidth size="small"
                value={form.company} onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Office ID" name="office" fullWidth size="small"
                value={form.office} onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Negotiator ID" name="negotiator" fullWidth size="small"
                value={form.negotiator} onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select label="Marketing Consent" name="marketing_consent" fullWidth size="small"
                value={form.marketing_consent} onChange={handleFormChange}
              >
                <MenuItem value="not_asked">Not Asked</MenuItem>
                <MenuItem value="granted">Granted</MenuItem>
                <MenuItem value="denied">Denied</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2, color: '#666' }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            sx={{
              borderRadius: 2, px: 4,
              background: 'linear-gradient(135deg, #6200EE, #9E47FF)',
              '&:hover': { background: 'linear-gradient(135deg, #3700B3, #6200EE)' },
            }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : editContact ? 'Save Changes' : 'Create Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{contactToDelete?.forename} {contactToDelete?.surname}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={handleDeleteConfirm}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
